import React, { useContext, useEffect, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import styled from "styled-components";
import {
  DiscountBannerWrapper,
  DiscountBannerIcon,
  DiscountBannerTitle
} from "@widgets/discountBanner/styles";
import { Context } from "@components/useContext/context";
import { resolvePath } from "@utils/resolvePath";
import { getInrFormat } from "@utils/inrFormatter";

/** Time the hero “Includes ₹X loyalty discount” stays before morphing */
export const LOYALTY_HERO_HOLD_MS = 1400;

const MORPH_MS = 720;

/** Hero hold + morph transition (+ small buffer) — full loyalty banner sequence */
export const LOYALTY_ANIMATION_SEQUENCE_MS =
  LOYALTY_HERO_HOLD_MS + MORPH_MS + 120;

const MorphTextStack = styled.div<{ $final: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: ${({ $final }) => ($final ? "flex-start" : "center")};
  flex: 1;
  min-height: ${({ $final }) => ($final ? "0" : "52px")};
  gap: ${({ $final }) => ($final ? "4px" : "0")};
  transition: justify-content ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    gap ${MORPH_MS}ms ease,
    min-height ${MORPH_MS}ms ease;
`;

const MorphMainTitle = styled(DiscountBannerTitle)<{ $final: boolean }>`
  max-height: ${({ $final }) => ($final ? "80px" : "0")};
  opacity: ${({ $final }) => ($final ? 1 : 0)};
  overflow: hidden;
  margin: 0;
  padding: 0;
  transition: max-height ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${Math.round(MORPH_MS * 0.85)}ms ease;
`;

const MorphLoyaltyLine = styled.div<{ $final: boolean }>`
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: ${({ $final }) => ($final ? 12 : 15)}px;
  font-style: normal;
  font-weight: ${({ $final }) => ($final ? 400 : 500)};
  line-height: 20px;
  color: var(--Text-Primary-grey600, #121212);
  transition: font-size ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    font-weight ${MORPH_MS}ms ease,
    transform ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: left bottom;
  transform: ${({ $final }) => ($final ? "scale(1)" : "scale(1.04)")};

  .discount-green {
    color: var(--Green-G400, #0b753e);
    transition: color ${MORPH_MS}ms ease;
  }

  ${({ $final }) =>
    $final &&
    `
    .discount-green {
      color: var(--Text-Primary-grey600, #121212);
    }
  `}
`;

type Props = {
  loyaltyAmt: number;
  totalSaveAmt: number;
  icon?: unknown;
  iconAlt?: string;
  styles?: React.CSSProperties;
  /** Fires after hero + morph finishes (M web backdrop uses this) */
  onAnimationSequenceComplete?: () => void;
};

/**
 * First shows prominent “Includes ₹X loyalty discount”, then scales it down to
 * subtitle size at the bottom while “You save ₹Y this year” fades in above
 * (matches renewal preview screenshots).
 */
const LoyaltyFinalMorphBanner = ({
  loyaltyAmt,
  totalSaveAmt,
  icon,
  iconAlt,
  styles,
  onAnimationSequenceComplete
}: Props) => {
  const { isDesktop } = useContext(Context);
  const [final, setFinal] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      setFinal(true);
      return undefined;
    }
    const t = window.setTimeout(() => setFinal(true), LOYALTY_HERO_HOLD_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!onAnimationSequenceComplete) return undefined;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const delay = reduceMotion ? 80 : LOYALTY_ANIMATION_SEQUENCE_MS;
    const t = window.setTimeout(onAnimationSequenceComplete, delay);
    return () => window.clearTimeout(t);
  }, [onAnimationSequenceComplete]);

  const layoutStyles = (() => {
    const s = { ...(styles || {}) } as React.CSSProperties;
    delete s.position;
    delete s.bottom;
    delete s.left;
    delete s.right;
    delete s.zIndex;
    delete s.transform;
    return { ...s, width: "100%", boxSizing: "border-box" as const };
  })();

  const totalSaveHeading = `You <span class="discount-green">save ₹${getInrFormat(
    totalSaveAmt
  )}</span> this year`;

  const loyaltyHeading = `Includes <span class="discount-green">₹${getInrFormat(
    loyaltyAmt
  )}</span> loyalty discount`;

  return (
    <DiscountBannerWrapper style={layoutStyles}>
      {icon ? (
        <DiscountBannerIcon
          src={resolvePath("/images/icons/increase_insuarance_price.svg")}
          alt={iconAlt || "discount"}
        />
      ) : null}
      <MorphTextStack $final={final}>
        <MorphMainTitle
          $isDesktop={isDesktop}
          $hasStrikeOff={false}
          $final={final}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(totalSaveHeading)
          }}
        />
        <MorphLoyaltyLine
          $final={final}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(loyaltyHeading)
          }}
        />
      </MorphTextStack>
    </DiscountBannerWrapper>
  );
};

export default LoyaltyFinalMorphBanner;
